import { useEffect, useState, useCallback, useMemo } from "react";
import { Toaster, toast } from "sonner";
import {
  Database, HardDrive, KeyRound, Upload, Trash2,
  LogOut, UserCog, Lock, RefreshCw, AlertTriangle, CheckCircle2, Image as ImageIcon, Gavel,
  Globe, Play, Pause, Info, Flag, X, ChevronRight, Maximize,
  Mail, UserX, BarChart3, ClipboardCheck,
} from "lucide-react";
import { supabase, supabaseConfigured } from "./lib/supabaseClient";
import { api, uploadFile, uploadPhoto, authedBlobUrl } from "./lib/api";
import { WorldExperience } from "./build5/WorldExperience";
import { forgetViewerData } from "./build5/api5";
import BetaInvite from "./beta/BetaInvite";

// Platform convention for network-backed actions: single-flight guard so a control can be disabled
// and a busy label shown while its action runs (prevents accidental duplicate submissions).
// `pending` holds the key of the in-flight action (or null); `run(key, fn)` executes with the guard.
function usePending() {
  const [pending, setPending] = useState(null);
  const run = async (key, fn) => {
    if (pending) return;
    setPending(key);
    try { return await fn(); } finally { setPending(null); }
  };
  return [pending, run];
}
import TvApp from "./tv/TvApp";
import { LinkApproval } from "./link/LinkApproval";
import ResetPassword from "./account/ResetPassword";
import { SiteFooter } from "./components/SiteFooter";
import { PoweredBy } from "./components/PoweredBy";
import { PasswordInput } from "./components/PasswordInput";
import { CountryPicker } from "./components/CountryPicker";
import { LOGIN } from "./constants/testIds";
import "./App.css";

function Stat({ icon: Icon, label, value, good }) {
  return (
    <div className="stat" data-testid={`stat-${label}`}>
      <Icon size={18} className={good ? "ok" : "warn"} />
      <div>
        <span className="stat-label">{label}</span>
        <span className={`stat-value ${good ? "ok" : "warn"}`}>{value}</span>
      </div>
    </div>
  );
}

// Admin UI dates are shown in UK format (dd/mm/yyyy) consistently.
const _gb = (v, opts) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString("en-GB", opts);
};
const fmtDateGB = (v) => _gb(v, { day: "2-digit", month: "2-digit", year: "numeric" });
const fmtDateTimeGB = (v) => _gb(v, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });


export function AuthPanel() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", display_name: "", age_band: "" });
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    setBlocked(null);
    try {
      if (mode === "register") {
        const r = await api("/auth/register", { method: "POST", auth: false, body: form });
        if (r.ok) toast.success("Registered. Check your email to verify, then log in.");
        else toast.error(r.data.detail || "Registration failed");
      } else {
        const r = await api("/auth/login", { method: "POST", auth: false, body: { email: form.email, password: form.password } });
        // Preserve existing non-2xx handling: show the backend detail (or a fallback).
        if (!r.ok) return toast.error(r.data.detail || "Login failed");
        // Establish a browser Supabase session so MFA + authed calls work.
        const { error } = await supabase.auth.setSession({ access_token: r.data.access_token, refresh_token: r.data.refresh_token });
        // Never claim success if the session did not actually establish.
        if (error) return toast.error("Could not complete sign in. Please try again.");
        // A deactivated (suspended) account authenticates with Supabase but WWW blocks it
        // server-side. Confirm the account is active BEFORE claiming success — otherwise
        // sign the session back out and show a clear branded message.
        const meRes = await api("/me");
        if (!meRes.ok) {
          await supabase.auth.signOut();
          if (meRes.status === 403) {
            setBlocked("Your account has been deactivated. Please contact the World Wide Wallpaper Administrator if you believe this is an error.");
          } else {
            toast.error("Could not complete sign in. Please try again.");
          }
          return;
        }
        toast.success("Logged in");
      }
    } catch (_e) {
      // Any thrown/rejected operation (network failure, setSession throw, etc.)
      // must surface a safe, generic error — never raw exception details.
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card auth" data-testid="auth-panel">
      <div className="tabs">
        <button data-testid="tab-login" className={mode === "login" ? "active" : ""} onClick={() => { setMode("login"); setBlocked(null); }}>Log in</button>
        <button data-testid="tab-register" className={mode === "register" ? "active" : ""} onClick={() => { setMode("register"); setBlocked(null); }}>Register</button>
      </div>
      {blocked && (
        <div className="auth-blocked" data-testid="deactivated-notice" role="alert">{blocked}</div>
      )}
      <input data-testid="input-email" placeholder="email" value={form.email} onChange={set("email")} />
      <PasswordInput testId="input-password" toggleTestId="login-password-toggle"
                     placeholder="password (min 8)" value={form.password} onChange={set("password")}
                     autoComplete={mode === "register" ? "new-password" : "current-password"} />
      {mode === "register" && (
        <>
          <input data-testid="input-display-name" placeholder="display name" value={form.display_name} onChange={set("display_name")} />
          <p className="field-hint" data-testid="display-name-help">This will be proudly displayed with your submitted pictures.</p>
          <label className="field-label">Age range</label>
          <select data-testid="input-age-band" value={form.age_band} onChange={set("age_band")}>
            <option value="">Select your age range</option>
            <option value="under_18">Under 18</option>
            <option value="18_or_over">18 or over</option>
          </select>
        </>
      )}
      <button data-testid="auth-submit" className="btn primary" disabled={busy} onClick={submit}>
        {busy ? "..." : mode === "register" ? "Create account" : "Log in"}
      </button>
      {mode === "login" && (
        <div className="auth-forgot">
          <a className="linklike" data-testid={LOGIN.forgotPasswordLink} href="/reset-password">
            Forgot password?
          </a>
        </div>
      )}
    </div>
  );
}

function MfaEnroll({ onDone }) {
  const [phase, setPhase] = useState("loading"); // loading | verify | enroll
  const [existingFactorId, setExistingFactorId] = useState(null);
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [factorId, setFactorId] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // A returning user who already has a VERIFIED factor must step up by verifying that
  // existing factor (challenge), NOT enrol a new one; a user with no factor enrols.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.mfa.listFactors();
      const verified = (data?.totp || []).find((f) => f.status === "verified");
      if (cancelled) return;
      if (verified) { setExistingFactorId(verified.id); setPhase("verify"); }
      else setPhase("enroll");
    })();
    return () => { cancelled = true; };
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: "totp" });
      if (error) return toast.error(error.message);
      setFactorId(data.id);
      setQr(data.totp.qr_code);
      setSecret(data.totp.secret);
    } finally { setBusy(false); }
  };

  const doVerify = async (fid, isEnroll) => {
    setBusy(true);
    try {
      const ch = await supabase.auth.mfa.challenge({ factorId: fid });
      if (ch.error) return toast.error(ch.error.message);
      const v = await supabase.auth.mfa.verify({ factorId: fid, challengeId: ch.data.id, code: code.trim() });
      if (v.error) return toast.error(v.error.message);
      await supabase.auth.refreshSession();
      if (isEnroll) { try { await api("/me/mfa/confirm", { method: "POST" }); } catch (_e) {} }
      toast.success(isEnroll ? "MFA enabled — admin access unlocked" : "Verified — admin access unlocked");
      onDone();
    } finally { setBusy(false); }
  };

  // Supabase returns qr_code as an SVG data URI (data:image/svg+xml;utf-8,<svg...>).
  // Re-encode the raw SVG into a valid data URI so it renders reliably as an <img> (crisp,
  // vector) instead of leaking the "data:image/svg+xml;utf-8," prefix as text.
  const qrSrc = qr && qr.startsWith("data:image/svg")
    ? "data:image/svg+xml," + encodeURIComponent(qr.substring(qr.indexOf(",") + 1))
    : qr;

  return (
    <div className="card" data-testid="mfa-enroll">
      <h3><Lock size={16} /> MFA required for privileged access</h3>
      {phase === "loading" && <p className="muted small">Checking your security settings…</p>}

      {phase === "verify" && (
        <>
          <p className="muted small" style={{ marginBottom: 10 }}>
            Enter the current 6-digit code from your authenticator app to unlock admin access.
          </p>
          <input data-testid="mfa-code" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} />
          <button data-testid="mfa-verify" className="btn primary" disabled={busy}
                  onClick={() => doVerify(existingFactorId, false)}>Verify</button>
        </>
      )}

      {phase === "enroll" && (!qr ? (
        <button data-testid="mfa-start" className="btn" disabled={busy} onClick={start}>Enroll authenticator (TOTP)</button>
      ) : (
        <>
          <p className="muted small" style={{ marginBottom: 10 }}>
            Scan this QR code with your authenticator app, then enter the 6-digit code it shows.
          </p>
          <img data-testid="mfa-qr" src={qrSrc} alt="TOTP QR code"
               width={220} height={220}
               style={{ display: "block", background: "#fff", padding: 12, borderRadius: 8 }} />
          <div style={{ margin: "10px 0 4px" }}>
            <button type="button" data-testid="mfa-cant-scan"
                    onClick={() => setShowKey((s) => !s)}
                    style={{ background: "none", border: 0, padding: 0, color: "inherit",
                             textDecoration: "underline", cursor: "pointer", fontSize: 13 }}>
              Can’t scan? Enter setup key manually
            </button>
            {showKey && secret && (
              <p data-testid="mfa-secret" className="muted"
                 style={{ marginTop: 6, fontFamily: "monospace", letterSpacing: 1, wordBreak: "break-all", fontSize: 13 }}>
                {secret}
              </p>
            )}
          </div>
          <input data-testid="mfa-code" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} />
          <button data-testid="mfa-verify" className="btn primary" disabled={busy}
                  onClick={() => doVerify(factorId, true)}>Verify & activate</button>
        </>
      ))}
    </div>
  );
}

function Uploads() {
  const [items, setItems] = useState([]);
  const [pending, run] = usePending();
  const [confirmDel, setConfirmDel] = useState(null);
  const load = useCallback(async () => {
    const r = await api("/uploads");
    if (r.ok) setItems(r.data.uploads || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = await uploadFile(f);
    if (r.ok) { toast.success("Uploaded to R2"); load(); }
    else toast.error(r.data.detail || "Upload failed");
  };
  const del = (id) => run(`del-${id}`, async () => {
    const r = await api(`/uploads/${id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Deleted from R2"); setConfirmDel(null); load(); }
  });

  return (
    <div className="card" data-testid="uploads-panel">
      <h3><HardDrive size={16} /> R2 Storage</h3>
      <label className="btn" data-testid="upload-btn">
        <Upload size={14} /> Upload file
        <input type="file" hidden onChange={onUpload} data-testid="upload-input" />
      </label>
      <ul className="list">
        {items.map((it) => (
          <li key={it.id} data-testid={`upload-${it.id}`}>
            <span>{it.original_filename} · {it.size_bytes}b</span>
            {confirmDel === it.id ? (
              <span className="row" style={{ gap: 6, display: "inline-flex", alignItems: "center" }} data-testid={`delete-confirm-row-${it.id}`}>
                <span className="muted small">Delete this object from R2?</span>
                <button className="btn danger" disabled={!!pending} onClick={() => del(it.id)} data-testid={`delete-confirm-${it.id}`}>{pending === `del-${it.id}` ? "Deleting…" : "Delete"}</button>
                <button className="btn" disabled={!!pending} onClick={() => setConfirmDel(null)} data-testid={`delete-cancel-${it.id}`}>Cancel</button>
              </span>
            ) : (
              <button className="icon" disabled={!!pending} onClick={() => setConfirmDel(it.id)} data-testid={`delete-${it.id}`}><Trash2 size={14} /></button>
            )}
          </li>
        ))}
        {!items.length && <li className="muted">No objects</li>}
      </ul>
    </div>
  );
}

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [minAge, setMinAge] = useState("");
  const [current, setCurrent] = useState(null);

  const load = useCallback(async () => {
    const u = await api("/admin/users");
    if (u.ok) setUsers(u.data.users || []);
    else if (u.status === 403) toast.error(u.data.detail);
    const m = await api("/admin/config/min-age");
    if (m.ok) setCurrent(m.data.minimum_contribution_age);
  }, []);
  useEffect(() => { load(); }, [load]);

  const [pending, run] = usePending();
  const saveAge = () => run("save-age", async () => {
    const r = await api("/admin/config/min-age", { method: "PUT", body: { minimum_contribution_age: Number(minAge) } });
    if (r.ok) { toast.success("Launch guard configured"); load(); }
    else toast.error(r.data.detail || "Failed");
  });
  const changeRole = (id, role) => run(`role-${id}`, async () => {
    const r = await api(`/admin/users/${id}/role`, { method: "PUT", body: { role } });
    if (r.ok) { toast.success("Role updated"); load(); }
    else toast.error(r.data.detail || "Failed");
  });
  const setStatus = (id, action) => run(`status-${id}`, async () => {
    const r = await api(`/admin/users/${id}/${action}`, { method: "POST" });
    if (r.ok) { toast.success(action === "suspend" ? "User deactivated" : "User reactivated"); load(); }
    else toast.error(r.data.detail || "Failed");
  });
  const [confirmDel, setConfirmDel] = useState(null);
  const deleteUser = (id) => run(`delete-${id}`, async () => {
    const r = await api(`/admin/users/${id}/delete`, { method: "POST", body: { confirm: true } });
    if (r.ok) { toast.success("User permanently deleted (anonymised)"); setConfirmDel(null); load(); }
    else toast.error(r.data.detail === "cannot_delete_admin" ? "Cannot delete another administrator"
                     : r.data.detail === "cannot_delete_self" ? "You cannot delete your own account here"
                     : r.data.detail || "Delete failed");
  });
  const STATUS_LABEL = { active: "Active", suspended: "Deactivated", deleted: "Deleted" };

  return (
    <div className="card" data-testid="admin-panel">
      <h3><UserCog size={16} /> Admin</h3>
      <div className="row">
        <span className="muted">minimum_contribution_age: <b>{current ?? "unset (launch blocked)"}</b></span>
      </div>
      <div className="row">
        <input data-testid="min-age-input" type="number" placeholder="e.g. 18" value={minAge} onChange={(e) => setMinAge(e.target.value)} />
        <button className="btn primary" data-testid="save-min-age" disabled={!!pending} onClick={saveAge}>{pending === "save-age" ? "Saving…" : "Set launch age"}</button>
      </div>
      <table className="tbl">
        <thead><tr><th>id</th><th>email</th><th>role</th><th>status</th><th>MFA</th><th></th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.www_user_id} data-testid={`user-${u.www_user_id}`}>
              <td>{u.www_user_id}</td><td>{u.email}</td><td>{u.role}</td>
              <td><span className={`badge s-${u.status}`} data-testid={`user-status-${u.www_user_id}`}>{STATUS_LABEL[u.status] || u.status}</span></td>
              <td>{u.mfa_enrolled ? "✓" : "—"}</td>
              <td>
                <select defaultValue={u.role} disabled={!!pending} onChange={(e) => changeRole(u.www_user_id, e.target.value)} data-testid={`role-${u.www_user_id}`}>
                  <option value="user">user</option>
                  <option value="moderator">moderator</option>
                  <option value="admin">admin</option>
                </select>
                {u.status === "active" && (
                  <button className="btn warn" data-testid={`suspend-${u.www_user_id}`} disabled={!!pending} onClick={() => setStatus(u.www_user_id, "suspend")}>{pending === `status-${u.www_user_id}` ? "Working…" : "Deactivate"}</button>
                )}
                {u.status === "suspended" && (
                  <button className="btn primary" data-testid={`reactivate-${u.www_user_id}`} disabled={!!pending} onClick={() => setStatus(u.www_user_id, "reactivate")}>{pending === `status-${u.www_user_id}` ? "Working…" : "Reactivate"}</button>
                )}
                {u.status !== "deleted" && u.role !== "admin" && (
                  confirmDel === u.www_user_id ? (
                    <span className="row" style={{ gap: 6, display: "inline-flex", alignItems: "center" }}>
                      <span className="muted small">Permanently delete &amp; anonymise?</span>
                      <button className="btn danger" data-testid={`delete-confirm-${u.www_user_id}`} disabled={!!pending} onClick={() => deleteUser(u.www_user_id)}>{pending === `delete-${u.www_user_id}` ? "Deleting…" : "Yes, delete"}</button>
                      <button className="btn" data-testid={`delete-cancel-${u.www_user_id}`} disabled={!!pending} onClick={() => setConfirmDel(null)}>Cancel</button>
                    </span>
                  ) : (
                    <button className="btn danger" data-testid={`delete-${u.www_user_id}`} disabled={!!pending} onClick={() => setConfirmDel(u.www_user_id)}>Delete</button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContributorTermsGate({ doc, onAccepted }) {
  const [agreed, setAgreed] = useState(false);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    if (!agreed || !doc) return;
    setBusy(true);
    const r = await api("/me/legal/accept", { method: "POST", body: { doc_type: doc.doc_type, version: doc.version } });
    setBusy(false);
    if (r.ok) { toast.success("Contributor Terms accepted — you can share a photograph now"); onAccepted(); }
    else toast.error(`Could not record acceptance: ${r.data.detail || r.status}`);
  };

  return (
    <div className="card" data-testid="contributor-terms-gate">
      <h3><Gavel size={16} /> Accept the Contributor Terms</h3>
      <p className="muted small" style={{ margin: "6px 0 10px" }}>
        Before sharing a photograph, please review and accept the current Contributor Terms.
      </p>
      {doc ? (
        <>
          <label className="chk" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                   data-testid="contributor-terms-checkbox" style={{ marginTop: 4 }} />
            <span className="muted" style={{ fontSize: 13 }}>
              I have read and accept the{" "}
              <button type="button" className="linklike" data-testid="contributor-terms-toggle"
                      onClick={() => setShow((s) => !s)}
                      style={{ background: "none", border: 0, color: "inherit", textDecoration: "underline", cursor: "pointer", padding: 0 }}>
                {doc.title.replace(/\s*\(DRAFT\)/, "")} ({doc.version})
              </button>.
            </span>
          </label>
          {show && (
            <p className="muted" data-testid="contributor-terms-body"
               style={{ whiteSpace: "pre-wrap", fontSize: 12, marginTop: 8, maxHeight: 200, overflow: "auto" }}>
              {doc.body}
            </p>
          )}
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn primary" onClick={accept} disabled={!agreed || busy}
                    data-testid="contributor-terms-accept">
              {busy ? "…" : "Accept & continue"}
            </button>
          </div>
        </>
      ) : (
        <p className="muted small">Loading the Contributor Terms…</p>
      )}
    </div>
  );
}

function PhotosPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState({});
  const [countries, setCountries] = useState([]);
  const [contribOutstanding, setContribOutstanding] = useState(null); // null=loading, []=none, [doc]=needs accept
  const [contribDoc, setContribDoc] = useState(null);

  const loadLegal = useCallback(async () => {
    const r = await api("/me/legal-status");
    if (r.ok) setContribOutstanding(r.data.outstanding_contribution || []);
    else setContribOutstanding([]);
  }, []);
  useEffect(() => { loadLegal(); }, [loadLegal]);
  useEffect(() => {
    if (contribOutstanding && contribOutstanding.length && !contribDoc) {
      api("/legal/documents/contributor_terms").then((r) => r.ok && setContribDoc(r.data));
    }
  }, [contribOutstanding, contribDoc]);

  const load = useCallback(async () => {
    const r = await api("/photos");
    if (r.ok) setItems(r.data.photos || []);
  }, []);
  const [stage, setStage] = useState(null); // {phase: 'uploading'|'processing', pct}
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api("/countries").then((r) => r.ok && setCountries(r.data.countries || [])); }, []);

  useEffect(() => {
    items.forEach(async (p) => {
      if (thumbs[p.photo_id] || p.status === "failed" || p.status === "processing") return;
      const url = await authedBlobUrl(`/photos/${p.photo_id}/variant/thumbnail`);
      if (url) setThumbs((t) => ({ ...t, [p.photo_id]: url }));
    });
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setStage({ phase: "uploading", pct: 0 });
    const r = await uploadPhoto(f, (phase, pct) => setStage({ phase, pct }));
    setBusy(false);
    setStage(null);
    e.target.value = "";
    if (r.ok) { toast.success("Uploaded & processed — add the details below"); load(); }
    else toast.error(`Upload rejected: ${r.data.detail || r.status}`);
  };

  // Contribution requires accepting the current Contributor Terms first (server-enforced).
  // While legal-status is loading, show a neutral placeholder so the upload control is
  // never shown prematurely to a user who still needs to accept the terms.
  if (contribOutstanding === null) {
    return (
      <div className="card" data-testid="photos-panel-loading">
        <h3><ImageIcon size={16} /> Photographs</h3>
        <p className="muted small">Loading…</p>
      </div>
    );
  }
  if (contribOutstanding.length) {
    return <ContributorTermsGate doc={contribDoc} onAccepted={loadLegal} />;
  }

  return (
    <div className="card" data-testid="photos-panel">
      <h3><ImageIcon size={16} /> Photographs</h3>
      <label className={`btn ${busy ? "is-busy" : ""}`} data-testid="photo-upload-btn" aria-busy={busy}>
        <Upload size={14} /> {busy ? "Uploading your photograph…" : "Upload photograph"}
        <input type="file" hidden accept="image/*,.heic,.heif" disabled={busy}
               onChange={onUpload} data-testid="photo-upload-input" />
      </label>
      {busy && (
        <div className="ww-upload-busy" data-testid="photo-upload-busy" role="status" aria-live="polite">
          <span className="ww-spinner" aria-hidden="true" />{" "}
          {stage?.phase === "processing"
            ? <span data-testid="upload-stage-processing">Processing image… creating wallpaper sizes. This can take a little while for large photos.</span>
            : <span data-testid="upload-stage-uploading">Uploading your photograph…{stage?.pct != null ? ` ${stage.pct}%` : ""}</span>}
        </div>
      )}
      <p className="muted small" style={{ margin: "6px 0 4px" }}>
        Upload a travel photo → add where &amp; when → confirm your rights → submit for review. Once approved and published it appears in the Wallpaper Player.
      </p>
      <div className="photo-grid">
        {items.map((p) => (
          <PhotoCard key={p.photo_id} p={p} thumb={thumbs[p.photo_id]} onChange={load} countries={countries} />
        ))}
        {!items.length && (
          <div className="ww-empty" data-testid="photos-empty" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "28px 16px", border: "1px dashed var(--ww-line)", borderRadius: 14 }}>
            <ImageIcon size={26} style={{ opacity: 0.5 }} />
            <p style={{ fontWeight: 600, margin: "8px 0 2px" }}>No photographs yet</p>
            <p className="muted small" style={{ margin: 0 }}>Upload your first travel photograph to get started — it only takes a moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function photoLifecycle(p) {
  if (p.status === "failed")
    return { label: "Upload failed", tone: "err", hint: "This photograph couldn't be processed. Please delete it and try uploading again." };
  if (p.publication_status === "published")
    return { label: "Live in the Wallpaper Player", tone: "ok", hint: "Your photograph is rotating in the viewer 🎉" };
  if (p.current_moderation_status === "approved")
    return { label: "Approved — awaiting publication", tone: "ok", hint: "A moderator will publish it into the Wallpaper Player shortly." };
  if (p.status === "ready_for_moderation")
    return { label: "In review", tone: "warn", hint: "Submitted — our moderators are taking a look." };
  if (p.status === "processing")
    return { label: "Processing…", tone: "", hint: "Preparing your photograph." };
  return { label: "Add details", tone: "warn", hint: "Tell us where & when it was taken, confirm your rights, then submit." };
}

function PhotoCard({ p, thumb, onChange, countries = [] }) {
  const [m, setM] = useState({
    country: p.country || "", region: p.region || "", city: p.city || "",
    place: p.place || "", date_taken: p.date_taken || (p.suggested && p.suggested.date_taken) || "",
    date_precision: p.date_precision || (p.suggested && p.suggested.date_precision) || "day",
  });
  const [rights, setRights] = useState(!!p.rights_attested);
  const set = (k) => (e) => setM({ ...m, [k]: e.target.value });
  const editable = p.status === "awaiting_metadata" || p.status === "processing";
  const life = photoLifecycle(p);
  const today = new Date().toISOString().slice(0, 10);
  const thisMonth = today.slice(0, 7);
  const years = useMemo(() => {
    const now = new Date().getFullYear(); const out = [];
    for (let y = now; y >= 1900; y--) out.push(y);
    return out;
  }, []);
  const canSubmit = !!m.country && !!m.date_taken && rights;

  // Precision is authoritative: switching precision re-anchors date_taken (YYYY-MM-01 / YYYY-01-01)
  // so an unknown day/month is never implied, and lets a contributor downgrade an EXIF-derived date.
  const setPrecision = (prec) => setM((cur) => {
    let nd = cur.date_taken || "";
    if (nd) {
      const [Y, M] = nd.split("-");
      if (prec === "year") nd = `${Y}-01-01`;
      else if (prec === "month") nd = `${Y}-${M || "01"}-01`;
    }
    return { ...cur, date_precision: prec, date_taken: nd };
  });
  const setExact = (e) => setM({ ...m, date_taken: e.target.value, date_precision: "day" });
  const setMonth = (e) => setM({ ...m, date_taken: e.target.value ? `${e.target.value}-01` : "", date_precision: "month" });
  const setYear = (e) => setM({ ...m, date_taken: e.target.value ? `${e.target.value}-01-01` : "", date_precision: "year" });

  const codeMap = useMemo(() => {
    const mm = {};
    countries.forEach((c) => { mm[c.name.toLowerCase()] = c.code; });
    return mm;
  }, [countries]);
  const resolveCode = (label) => (label ? codeMap[label.trim().toLowerCase()] || null : null);
  const [pending, run] = usePending();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const persist = async () => {
    const code = resolveCode(m.country);
    if (m.country && !code) { toast.error("Please choose a country from the list"); return false; }
    await api(`/photos/${p.photo_id}`, { method: "PATCH", body: { ...m, country_code: code, date_confirmed: true } });
    if (rights) await api(`/photos/${p.photo_id}/attest-rights`, { method: "POST", body: { accepted: true } });
    return true;
  };
  const save = () => run("save", async () => { if (await persist()) { toast.success("Saved"); onChange(); } });
  const submit = () => run("submit", async () => {
    if (!(await persist())) return;
    const r = await api(`/photos/${p.photo_id}/submit`, { method: "POST" });
    if (r.ok) { toast.success("Submitted for moderation"); onChange(); }
    else toast.error(`Cannot submit: ${r.data.detail || r.status}`);
  });
  const del = () => run("del", async () => { await api(`/photos/${p.photo_id}`, { method: "DELETE" }); toast.success("Deleted"); onChange(); });
  const anyBusy = !!pending;

  const deleteControl = confirmDelete ? (
    <span className="row" style={{ gap: 6, display: "inline-flex", alignItems: "center", flexWrap: "wrap" }} data-testid={`del-confirm-row-${p.photo_id}`}>
      <span className="muted small">Delete this photo? This will remove it from your account.</span>
      <button className="btn danger" disabled={anyBusy} onClick={del} data-testid={`del-confirm-${p.photo_id}`}>{pending === "del" ? "Deleting…" : "Delete"}</button>
      <button className="btn" disabled={anyBusy} onClick={() => setConfirmDelete(false)} data-testid={`del-cancel-${p.photo_id}`}>Cancel</button>
    </span>
  ) : (
    <button className="icon" disabled={anyBusy} onClick={() => setConfirmDelete(true)} data-testid={`del-${p.photo_id}`}><Trash2 size={14} /></button>
  );

  return (
    <div className="photo-card" data-testid={`photo-${p.photo_id}`}>
      <div className="photo-thumb">
        {p.status === "failed"
          ? <div className="ph ph-failed" data-testid={`photo-failed-${p.photo_id}`}>Upload failed</div>
          : (thumb ? <img src={thumb} alt="thumbnail" /> : <div className="ph" />)}
      </div>
      <div className="photo-body">
        <div className="badges">
          <span className={`badge ${life.tone === "ok" ? "s-ok" : "s-" + p.status}`} data-testid={`photo-status-${p.photo_id}`}>{life.label}</span>
          {p.gps_available && <span className="badge gps">GPS · private</span>}
        </div>
        <p className="muted small" style={{ margin: "2px 0 10px" }}>{life.hint}</p>
        {editable ? (
          <>
            <label className="field-label">Where was this taken? *</label>
            <CountryPicker countries={countries} value={m.country} onChange={(name) => setM((cur) => ({ ...cur, country: name }))} testId={`country-${p.photo_id}`} />
            <label className="field-label">When was it taken? *</label>
            <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 8 }} data-testid={`date-precision-${p.photo_id}`}>
              {[["day", "Exact date"], ["month", "Month & year"], ["year", "Year only"]].map(([val, label]) => (
                <button key={val} type="button" className={`btn ${m.date_precision === val ? "primary" : ""}`}
                  onClick={() => setPrecision(val)} data-testid={`date-precision-${val}-${p.photo_id}`}>{label}</button>
              ))}
            </div>
            {m.date_precision === "day" && (
              <input type="date" max={today} value={m.date_taken} onChange={setExact} data-testid={`date-${p.photo_id}`} />
            )}
            {m.date_precision === "month" && (
              <input type="month" max={thisMonth} value={m.date_taken ? m.date_taken.slice(0, 7) : ""} onChange={setMonth} data-testid={`date-month-${p.photo_id}`} />
            )}
            {m.date_precision === "year" && (
              <select value={m.date_taken ? m.date_taken.slice(0, 4) : ""} onChange={setYear} data-testid={`date-year-${p.photo_id}`}>
                <option value="">Select a year…</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            <input placeholder="Region / State (optional)" value={m.region} onChange={set("region")} />
            <input placeholder="City / Town (optional)" value={m.city} onChange={set("city")} />
            <input placeholder="Place / Landmark (optional)" value={m.place} onChange={set("place")} />
            <div className={`rights-callout ${rights ? "confirmed" : ""}`} data-testid={`rights-callout-${p.photo_id}`}>
              <div className="rights-callout-step">
                {rights ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                {rights ? "Rights confirmed" : "Required next step"}
              </div>
              <label className="rights-callout-label">
                <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} data-testid={`rights-${p.photo_id}`} />
                <span>
                  <b>I own or have the rights to submit this photograph.</b> You must confirm this before you can submit it for review.
                  <span className="rights-callout-note"> (Final terms pending legal review.)</span>
                </span>
              </label>
            </div>
            <div className="row">
              <button className="btn" onClick={save} disabled={anyBusy} data-testid={`save-${p.photo_id}`}>{pending === "save" ? "Saving…" : "Save draft"}</button>
              <button className="btn primary" onClick={submit} disabled={!canSubmit || anyBusy} data-testid={`submit-${p.photo_id}`}>{pending === "submit" ? "Submitting…" : "Submit for moderation"}</button>
              {deleteControl}
            </div>
            {!canSubmit && <p className="muted small" style={{ marginTop: 6 }}>Add a country, a date, and confirm your rights to submit.</p>}
          </>
        ) : (
          <>
            <div className="muted">{[m.city, m.region, m.country].filter(Boolean).join(", ") || "—"}</div>
            <div className="muted">Taken: {p.date_display || p.date_taken || "—"}</div>
            {deleteControl}
          </>
        )}
      </div>
    </div>
  );
}

const MOD_CHECKLIST_ITEMS = [
  "Weapons / realistic weapon depiction",
  "Threats, violence or graphic/disturbing content",
  "Self-harm or suicide content",
  "Extremist, hateful or discriminatory symbols/text",
  "Other prohibited text (drugs, illegal sale, explicit sexual)",
  "Personal data / PII (faces, plates, e-mail, phone, precise location)",
];

function ModeratorChecklist({ photoId }) {
  const [checked, setChecked] = useState({});
  useEffect(() => { setChecked({}); }, [photoId]); // reset per selected photo
  return (
    <div className="card" data-testid="moderator-checklist" style={{ borderLeft: "3px solid #c0456b", marginBottom: 12 }}>
      <h4 style={{ display: "flex", alignItems: "center", gap: 6, margin: "0 0 4px" }}>
        <ClipboardCheck size={15} /> Reviewer checklist — human aid only
      </h4>
      <p className="muted small" style={{ marginTop: 0 }}>
        Automated scanning is weak at these; confirm each manually. This does not approve, reject, score or publish anything.
      </p>
      <ul className="list">
        {MOD_CHECKLIST_ITEMS.map((t, i) => (
          <li key={i}>
            <label className="chk" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <input type="checkbox" data-testid={`checklist-item-${i}`}
                     checked={!!checked[i]} onChange={() => setChecked((c) => ({ ...c, [i]: !c[i] }))} />
              <span className="small">{t}</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ModerationPanel() {
  const [queue, setQueue] = useState([]);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [sel, setSel] = useState(null);        // selected photo_id
  const [detail, setDetail] = useState(null);
  const [img, setImg] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [rnote, setRnote] = useState("");
  const [busy, setBusy] = useState(false);
  const [qthumbs, setQthumbs] = useState({});
  // Web UX package: Compact / Large queue view, multi-select + bulk approval.
  const [view, setView] = useState(() => localStorage.getItem("ww.modQueueView") || "compact");
  const [selected, setSelected] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("ww.modQueueView", v); };
  const toggleSelected = (pid) => setSelected((s) => { const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n; });
  const allSelected = queue.length > 0 && queue.every((q) => selected.has(q.photo_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(queue.map((q) => q.photo_id)));

  const loadQueue = useCallback(async () => {
    const r = await api("/moderation/queue");
    if (r.ok) {
      const list = r.data.queue || [];
      setQueue(list);
      // Drop selections for photographs no longer in the queue.
      setSelected((s) => new Set([...s].filter((id) => list.some((q) => q.photo_id === id))));
    }
    else if (r.status === 403) toast.error(r.data.detail || "Not authorized");
  }, []);

  const bulkApprove = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    setBusy(true);
    try {
      const r = await api("/moderation/bulk-approve", { method: "POST", body: { photo_ids: ids } });
      if (r.ok) {
        const { approved, published, failed } = r.data;
        if (approved.length) toast.success(`Approved ${approved.length} photograph${approved.length === 1 ? "" : "s"}${published.length ? ` · ${published.length} published` : ""}.`);
        if (failed.length) toast.error(`${failed.length} could not be approved.`);
        if (sel && approved.includes(sel)) { setSel(null); setDetail(null); setImg(null); setReason(""); setNote(""); }
        setSelected(new Set());
        setConfirmBulk(false);
        await loadQueue();
        if (approved.length) window.dispatchEvent(new Event("ww:pub-refresh"));
      } else toast.error(r.data.detail || "Bulk approval failed");
    } finally { setBusy(false); }
  };

  // Item 6 — fetch a small thumbnail for each photograph awaiting moderation.
  useEffect(() => {
    queue.forEach(async (q) => {
      if (qthumbs[q.photo_id]) return;
      const url = await authedBlobUrl(`/moderation/photos/${q.photo_id}/image?variant=thumbnail`);
      if (url) setQthumbs((t) => ({ ...t, [q.photo_id]: url }));
    });
  }, [queue]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadQueue();
    api("/moderation/reason-codes").then((r) => r.ok && setReasonCodes(r.data.reason_codes || []));
  }, [loadQueue]);

  const openDetail = useCallback(async (pid) => {
    setSel(pid); setDetail(null); setImg(null); setReason(""); setNote("");
    const r = await api(`/moderation/photos/${pid}`);
    if (r.ok) setDetail(r.data);
    const url = await authedBlobUrl(`/moderation/photos/${pid}/image?variant=desktop`);
    if (url) setImg(url);
  }, []);

  const decide = async (action, needReason) => {
    if (needReason && !reason) return toast.error("Select a reason code first");
    setBusy(true);
    try {
      const r = await api(`/moderation/photos/${sel}/decision`, {
        method: "POST",
        body: { action, reason_code: needReason ? reason : undefined, note: note || undefined },
      });
      if (r.ok) {
        const ns = r.data.current_moderation_status;
        if (action === "approve" && r.data.published) toast.success("Approved & published — the contributor has been notified.");
        else toast.success(`${action} → ${ns}`);
        await loadQueue();
        const MODERATABLE = ["pending_scan", "in_review", "suspended"];
        if (MODERATABLE.includes(ns)) {
          await openDetail(sel);
        } else {
          // Approved / rejected / removed: this photo has left the moderation
          // queue — return the moderator to the list and clear the stale detail
          // so the same image can't be repeatedly approved (no duplicate events).
          setSel(null); setDetail(null); setImg(null); setReason(""); setNote("");
          if (ns === "approved") window.dispatchEvent(new Event("ww:pub-refresh"));
        }
      }
      else toast.error(r.data.detail || `${action} failed`);
    } finally { setBusy(false); }
  };

  const rescan = async () => {
    setBusy(true);
    try {
      const r = await api(`/moderation/photos/${sel}/scan`, { method: "POST" });
      if (r.ok) { toast.success("Re-scanned"); await openDetail(sel); }
    } finally { setBusy(false); }
  };

  // Report-handling actions, SEPARATE from ordinary moderation controls.
  const resolveReport = async (resolution, reportId) => {
    setBusy(true);
    try {
      const r = await api(`/moderation/photos/${sel}/reports/resolve`, {
        method: "POST",
        body: { resolution, report_id: reportId, note: rnote || undefined },
      });
      if (r.ok) {
        if (resolution === "dismiss") {
          toast.success(r.data.restored
            ? "Report dismissed — photograph returned to the Viewer"
            : "Report dismissed");
        } else {
          toast.success(resolution === "suspend"
            ? "Photograph suspended — open reports actioned"
            : "Photograph removed — open reports actioned");
        }
        setRnote("");
        await loadQueue();
        const ns = r.data.current_moderation_status;
        const MODERATABLE = ["pending_scan", "in_review", "suspended"];
        if (MODERATABLE.includes(ns)) {
          await openDetail(sel);
        } else {
          setSel(null); setDetail(null); setImg(null); setReason(""); setNote("");
          if (ns === "approved") window.dispatchEvent(new Event("ww:pub-refresh"));
        }
      } else toast.error(r.data.detail || "Report resolution failed");
    } finally { setBusy(false); }
  };

  const st = detail?.current_moderation_status;
  const terminal = st === "removed";
  const latestScan = detail?.events?.filter((e) => e.event_type === "auto_scan").slice(-1)[0];

  return (
    <div className="card" data-testid="moderation-panel">
      <h3><Gavel size={16} /> Moderation review</h3>
      <div className={`mod-layout ${view === "large" ? "mod-layout-large" : ""}`}>
        <div className={`mod-queue ${view === "large" ? "mod-queue-large" : ""}`} data-testid="moderation-queue">
          <div className="row between wrap">
            <span className="muted" data-testid="mod-queue-count">Queue · {queue.length}</span>
            <div className="row" style={{ gap: 6 }}>
              <div className="mod-view-toggle" role="group" aria-label="Queue view">
                <button className={view === "compact" ? "active" : ""} onClick={() => setViewPersist("compact")} data-testid="mod-view-compact">Compact</button>
                <button className={view === "large" ? "active" : ""} onClick={() => setViewPersist("large")} data-testid="mod-view-large">Large</button>
              </div>
              <button className="icon" onClick={loadQueue} data-testid="mod-refresh-queue"><RefreshCw size={13} /></button>
            </div>
          </div>
          {queue.length > 0 && (
            <div className="mod-bulk" data-testid="mod-bulk-bar">
              <label className="chk small">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="mod-select-all" /> Select all
              </label>
              <span className="muted small" data-testid="mod-selected-count">{selected.size} selected</span>
              {!confirmBulk ? (
                <button className="btn primary small" disabled={busy || selected.size === 0} onClick={() => setConfirmBulk(true)} data-testid="mod-bulk-approve">
                  Approve selected{selected.size ? ` (${selected.size})` : ""}
                </button>
              ) : (
                <div className="mod-bulk-confirm" data-testid="mod-bulk-confirm">
                  <span>Approve {selected.size} selected photograph{selected.size === 1 ? "" : "s"}?</span>
                  <button className="btn primary small" disabled={busy} onClick={bulkApprove} data-testid="mod-bulk-confirm-yes">{busy ? "Approving…" : "Yes, approve"}</button>
                  <button className="btn small" disabled={busy} onClick={() => setConfirmBulk(false)} data-testid="mod-bulk-confirm-cancel">Cancel</button>
                </div>
              )}
            </div>
          )}
          {view === "large" ? (
            <div className="mod-grid" data-testid="mod-queue-grid">
              {queue.map((q) => (
                <div key={q.photo_id} className={`mod-grid-card ${sel === q.photo_id ? "active" : ""} ${selected.has(q.photo_id) ? "checked" : ""}`} data-testid={`mod-queue-item-${q.photo_id}`}>
                  <label className="mod-grid-check" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(q.photo_id)} onChange={() => toggleSelected(q.photo_id)} data-testid={`mod-select-${q.photo_id}`} />
                  </label>
                  <button type="button" className="mod-grid-img" onClick={() => openDetail(q.photo_id)} data-testid={`mod-queue-thumb-${q.photo_id}`} aria-label="Open detailed review">
                    {qthumbs[q.photo_id] ? <img src={qthumbs[q.photo_id]} alt="" /> : <span className="ph" />}
                  </button>
                  <div className="mod-grid-meta">
                    <b data-testid={`mod-grid-location-${q.photo_id}`}>{[q.place, q.city, q.region, q.country].filter(Boolean).join(", ") || "Location not given"}</b>
                    <span data-testid={`mod-grid-date-${q.photo_id}`}>{q.date_display || fmtDateGB(q.date_taken) || "Date not given"}</span>
                    <span className="muted" data-testid={`mod-grid-contributor-${q.photo_id}`}>by {q.contributor_handle}</span>
                    <div className="badges">
                      <span className={`badge s-${q.current_moderation_status}`}>{q.current_moderation_status.replace(/_/g, " ")}</span>
                      {q.open_reports > 0 && <span className="badge report" data-testid={`mod-reports-${q.photo_id}`}>{q.open_reports} report{q.open_reports > 1 ? "s" : ""}</span>}
                    </div>
                  </div>
                </div>
              ))}
              {!queue.length && <p className="muted">Queue empty</p>}
            </div>
          ) : (
          <ul className="list">
            {queue.map((q) => (
              <li key={q.photo_id}
                  className={`mod-q-item ${sel === q.photo_id ? "active" : ""}`}
                  data-testid={`mod-queue-item-${q.photo_id}`}
                  onClick={() => openDetail(q.photo_id)}>
                <input type="checkbox" className="mod-q-check" checked={selected.has(q.photo_id)}
                       onClick={(e) => e.stopPropagation()} onChange={() => toggleSelected(q.photo_id)}
                       data-testid={`mod-select-${q.photo_id}`} aria-label="Select photograph" />
                <span className="mod-q-thumb" data-testid={`mod-queue-thumb-${q.photo_id}`}>
                  {qthumbs[q.photo_id] ? <img src={qthumbs[q.photo_id]} alt="" /> : <span className="ph" />}
                </span>
                <span className={`badge s-${q.current_moderation_status}`}>{q.current_moderation_status.replace(/_/g, " ")}</span>
                <span className="mono small">{q.photo_id.slice(0, 8)}</span>
                {q.open_reports > 0 && <span className="badge report" data-testid={`mod-reports-${q.photo_id}`}>{q.open_reports} report{q.open_reports > 1 ? "s" : ""}</span>}
              </li>
            ))}
            {!queue.length && <li className="muted">Queue empty</li>}
          </ul>
          )}
        </div>

        <div className="mod-detail" data-testid="moderation-detail">
          {!detail ? (
            <p className="muted">Select a photograph from the queue to review.</p>
          ) : (
            <>
              <div className="mod-image">{img ? <img src={img} alt="review" data-testid="mod-review-image" /> : <div className="ph" />}</div>
              <ModeratorChecklist photoId={sel} />
              <div className="badges">
                <span className={`badge s-${st}`} data-testid="mod-current-status">{st.replace(/_/g, " ")}</span>
                <span className={`badge ${detail.publishable ? "gps" : ""}`} data-testid="mod-publishable">{detail.publishable ? "publishable" : "not publishable"}</span>
              </div>
              <div className="kv"><span>contributor</span><b className="mono">{detail.contributor.slice(0, 12)}…</b></div>
              <div className="kv"><span>location</span><b>{[detail.location.city, detail.location.region, detail.location.country].filter(Boolean).join(", ") || "—"}</b></div>
              <div className="kv"><span>date taken</span><b>{detail.date_display || fmtDateGB(detail.date_taken)}</b>{detail.date_precision && detail.date_precision !== "day" && <span className="muted small"> · {detail.date_precision} precision</span>}</div>
              <div className="kv"><span>auto-scan</span>
                <b data-testid="mod-scan-result">
                  {latestScan
                    ? (latestScan.result?._ok === false ? "provider error (fail-closed)" : (latestScan.result?.flagged ? "flagged" : "clean"))
                    : "—"}
                </b>
              </div>

              <div className="mod-controls">
                <label className="field-label">Reason code</label>
                <select className="reason-select" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="mod-reason-select">
                  <option value="">— select —</option>
                  {reasonCodes.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                </select>
                <input placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="mod-note" />
                <div className="row wrap">
                  <button className="btn primary" disabled={busy || terminal} onClick={() => decide("approve", false)} data-testid="mod-approve">Approve &amp; Publish</button>
                  <button className="btn danger" disabled={busy || terminal} onClick={() => decide("reject", true)} data-testid="mod-reject">Reject</button>
                  <button className="btn warn" disabled={busy || terminal} onClick={() => decide("suspend", true)} data-testid="mod-suspend">Suspend</button>
                  <button className="btn" disabled={busy || terminal} onClick={() => decide("reinstate", false)} data-testid="mod-reinstate">Reinstate</button>
                  <button className="btn danger" disabled={busy || terminal} onClick={() => decide("remove", true)} data-testid="mod-remove">Remove</button>
                  <button className="btn ghost" disabled={busy} onClick={rescan} data-testid="mod-rescan"><RefreshCw size={13} /> Re-scan</button>
                </div>
                {terminal && <p className="muted" data-testid="mod-terminal">This photograph was removed (terminal state).</p>}
              </div>

              {detail.reports?.length > 0 && (
                <div className="mod-reports" data-testid="moderation-reports">
                  <label className="field-label">
                    Report details <span className="muted small" data-testid="mod-report-count">
                      · {detail.report_count} report{detail.report_count === 1 ? "" : "s"} · {detail.open_report_count} open
                    </span>
                  </label>
                  <p className="muted small mod-report-help" data-testid="mod-report-help">
                    Dismissing a report takes no action against the photograph. If no other open
                    reports remain, an approved photograph will return to the Viewer. Suspending or
                    removing a photograph will take it out of the Viewer.
                  </p>
                  <ul className="list mod-report-list">
                    {detail.reports.map((r) => (
                      <li key={r.id} className="mod-report-item" data-testid={`mod-report-${r.id}`}>
                        <div className="mod-report-head">
                          <span className="badge report">{r.reason_code.replace(/_/g, " ")}</span>
                          <span className={`badge rs-${r.status}`} data-testid={`mod-report-status-${r.id}`}>
                            {r.status === "open" ? "Open" : r.status === "actioned" ? "Actioned" : "Dismissed"}
                          </span>
                          <span className="muted small">{fmtDateTimeGB(r.created_at)}</span>
                          <span className="muted small">· {r.reporter}</span>
                        </div>
                        {r.detail && <div className="mod-report-comment">“{r.detail}”</div>}
                        {r.status === "open" && (
                          <div className="row wrap mod-report-actions">
                            <button className="btn small" disabled={busy}
                                    onClick={() => resolveReport("dismiss", r.id)}
                                    data-testid={`mod-report-dismiss-${r.id}`}>
                              Dismiss / No action required
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  {detail.open_report_count > 0 && (
                    <div className="mod-report-photo-actions">
                      <input placeholder="resolution note (optional)" value={rnote}
                             onChange={(e) => setRnote(e.target.value)} data-testid="mod-report-note" />
                      <div className="row wrap">
                        <button className="btn warn" disabled={busy || terminal}
                                onClick={() => resolveReport("suspend")} data-testid="mod-report-suspend">
                          Suspend pending review
                        </button>
                        <button className="btn danger" disabled={busy || terminal}
                                onClick={() => resolveReport("remove")} data-testid="mod-report-remove">
                          Remove photograph
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mod-events" data-testid="moderation-events">
                <label className="field-label">Event history (append-only)</label>
                <ul className="list">
                  {detail.events.map((e, i) => (
                    <li key={i} data-testid={`mod-event-${i}`}>
                      <span className={`badge e-${e.event_type}`}>{e.event_type.replace(/_/g, " ")}</span>
                      <span className="small">{e.action || ""} {e.reason_code ? `· ${e.reason_code}` : ""} {e.actor_role ? `· ${e.actor_role}` : ""}</span>
                      <span className="muted small">{fmtDateTimeGB(e.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PublicationPanel() {
  const [q, setQ] = useState({ approved_unpublished: [], published: [] });
  const load = useCallback(async () => { const r = await api("/publication/queue"); if (r.ok) setQ(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  // Refresh when a moderator approves a photo elsewhere on the page, so approved
  // images reliably appear here without a full page reload (#5).
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ww:pub-refresh", h);
    return () => window.removeEventListener("ww:pub-refresh", h);
  }, [load]);
  const pub = async (id) => { const r = await api(`/publication/${id}/publish`, { method: "POST" }); r.ok ? toast.success("Published") : toast.error(r.data.detail || "Failed"); load(); };
  const unpub = async (id) => { const r = await api(`/publication/${id}/unpublish`, { method: "POST" }); r.ok ? toast.success("Unpublished") : toast.error(r.data.detail || "Failed"); load(); };
  return (
    <div className="card" data-testid="publication-panel">
      <div className="row between">
        <h3><Globe size={16} /> Publication queue</h3>
        <button className="icon" data-testid="pub-refresh-queue" onClick={load} aria-label="Refresh publication queue"><RefreshCw size={13} /></button>
      </div>
      {q.approved_unpublished.length > 0 && (
        <div className="ww-callout" data-testid="publish-callout" style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "rgba(80,160,120,0.12)", border: "1px solid rgba(80,160,120,0.4)", borderRadius: 12, padding: "10px 12px", margin: "4px 0 12px" }}>
          <AlertTriangle size={16} style={{ marginTop: 2 }} />
          <span className="small"><b>{q.approved_unpublished.length} photograph(s) approved and waiting.</b> Publishing is the next step — click <b>Publish</b> to make them live in the Wallpaper Player.</span>
        </div>
      )}
      <label className="field-label">Approved · needs publishing ({q.approved_unpublished.length})</label>
      <ul className="list">
        {q.approved_unpublished.map((p) => (
          <li key={p.photo_id} data-testid={`pub-item-${p.photo_id}`}>
            <span className="small">{[p.place, p.city, p.country].filter(Boolean).join(", ") || p.country || p.photo_id.slice(0, 8)}</span>
            <button className="btn primary" data-testid={`publish-${p.photo_id}`} onClick={() => pub(p.photo_id)}>Publish →</button>
          </li>))}
        {!q.approved_unpublished.length && <li className="muted">Nothing waiting to publish.</li>}
      </ul>
      <label className="field-label">Live in the Wallpaper Player ({q.published.length})</label>
      <ul className="list">
        {q.published.map((p) => (
          <li key={p.photo_id} data-testid={`published-${p.photo_id}`}>
            <span className="small">{[p.place, p.city, p.country].filter(Boolean).join(", ") || p.country || p.photo_id.slice(0, 8)}</span>
            <button className="btn danger" data-testid={`unpublish-${p.photo_id}`} onClick={() => unpub(p.photo_id)}>Unpublish</button>
          </li>))}
        {!q.published.length && <li className="muted">None yet.</li>}
      </ul>
    </div>
  );
}

function SubscriberPanel() {
  const [users, setUsers] = useState([]);
  const load = useCallback(async () => { const r = await api("/admin/subscribers"); if (r.ok) setUsers(r.data.users || []); }, []);
  useEffect(() => { load(); }, [load]);
  const grant = async (id) => { const r = await api(`/admin/subscribers/${id}/grant`, { method: "POST" }); r.ok ? toast.success("Subscriber granted") : toast.error(r.data.detail || "Failed"); load(); };
  const revoke = async (id) => { const r = await api(`/admin/subscribers/${id}/revoke`, { method: "POST" }); r.ok ? toast.success("Subscriber revoked") : toast.error(r.data.detail || "Failed"); load(); };
  return (
    <div className="card" data-testid="subscriber-panel">
      <h3><UserCog size={16} /> Subscribers</h3>
      <table className="tbl">
        <thead><tr><th>email</th><th>subscriber</th><th></th></tr></thead>
        <tbody>{users.map((u) => (
          <tr key={u.www_user_id} data-testid={`sub-row-${u.www_user_id}`}>
            <td>{u.email}</td><td>{u.is_subscriber ? "✓" : "—"}</td>
            <td>{u.is_subscriber
              ? <button className="btn" data-testid={`revoke-${u.www_user_id}`} onClick={() => revoke(u.www_user_id)}>Revoke</button>
              : <button className="btn primary" data-testid={`grant-${u.www_user_id}`} onClick={() => grant(u.www_user_id)}>Grant</button>}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function InvitationsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ email: "", capability: "subscriber" });
  const load = useCallback(async () => { const r = await api("/admin/invitations"); if (r.ok) setItems(r.data.invitations || []); }, []);
  useEffect(() => { load(); }, [load]);
  const issue = async () => {
    if (!form.email) return toast.error("Email required");
    const r = await api("/admin/invitations", { method: "POST", body: form });
    if (r.ok) { toast.success(r.data.manual_delivery ? "Invitation created (manual link)" : "Invitation sent"); if (r.data.invite_link) toast.message(r.data.invite_link); setForm({ email: "", capability: "subscriber" }); load(); }
    else toast.error(r.data.detail || "Failed to issue");
  };
  const revoke = async (id) => { const r = await api(`/admin/invitations/${id}/revoke`, { method: "POST" }); r.ok ? toast.success("Revoked") : toast.error(r.data.detail || "Failed"); load(); };
  const reissue = async (id) => { const r = await api(`/admin/invitations/${id}/reissue`, { method: "POST" }); if (r.ok) { toast.success("Reissued"); if (r.data.invite_link) toast.message(r.data.invite_link); } else toast.error(r.data.detail || "Failed"); load(); };
  return (
    <div className="card" data-testid="invitations-panel">
      <h3><Mail size={16} /> Beta invitations</h3>
      <div className="row wrap">
        <input placeholder="invitee email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" />
        <select value={form.capability} onChange={(e) => setForm({ ...form, capability: e.target.value })} data-testid="invite-capability">
          <option value="subscriber">subscriber</option>
          <option value="contributor">contributor</option>
          <option value="both">both</option>
        </select>
        <button className="btn primary" onClick={issue} data-testid="invite-issue">Issue</button>
      </div>
      <table className="tbl">
        <thead><tr><th>email</th><th>capability</th><th>status</th><th>expires</th><th></th></tr></thead>
        <tbody>{items.map((i) => (
          <tr key={i.id} data-testid={`invite-row-${i.id}`}>
            <td>{i.email}</td><td>{i.capability}</td>
            <td><span className={`badge s-${i.status}`} data-testid={`invite-status-${i.id}`}>{i.status}</span></td>
            <td className="small">{fmtDateGB(i.expires_at)}</td>
            <td>
              {i.status === "pending" && <button className="btn" data-testid={`invite-revoke-${i.id}`} onClick={() => revoke(i.id)}>Revoke</button>}
              <button className="btn ghost" data-testid={`invite-reissue-${i.id}`} onClick={() => reissue(i.id)}>Reissue</button>
            </td>
          </tr>))}
          {!items.length && <tr><td colSpan="5" className="muted">No invitations</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function DeletionsPanel() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { const r = await api("/admin/account-deletions"); if (r.ok) setItems(r.data.deletions || []); }, []);
  useEffect(() => { load(); }, [load]);
  const retry = async (id) => { const r = await api(`/admin/account-deletions/${id}/retry`, { method: "POST" }); r.ok ? toast.success(`Retry → ${r.data.status}`) : toast.error(r.data.detail || "Failed"); load(); };
  return (
    <div className="card" data-testid="deletions-panel">
      <h3><UserX size={16} /> Account deletions</h3>
      <table className="tbl">
        <thead><tr><th>id</th><th>status</th><th>pg</th><th>r2</th><th>auth</th><th></th></tr></thead>
        <tbody>{items.map((d) => (
          <tr key={d.id} data-testid={`deletion-row-${d.id}`}>
            <td className="mono small">{d.id.slice(0, 8)}</td>
            <td><span className={`badge s-${d.status}`}>{d.status}</span></td>
            <td>{d.pg_done ? "✓" : "—"}</td><td>{d.r2_done ? "✓" : "—"}</td><td>{d.auth_done ? "✓" : "—"}</td>
            <td>{d.status !== "completed" && <button className="btn" data-testid={`deletion-retry-${d.id}`} onClick={() => retry(d.id)}>Retry</button>}</td>
          </tr>))}
          {!items.length && <tr><td colSpan="6" className="muted">None</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AnalyticsPanel() {
  const [s, setS] = useState(null);
  const load = useCallback(async () => { const r = await api("/admin/analytics/summary"); if (r.ok) setS(r.data); }, []);
  useEffect(() => { load(); }, [load]);
  const [pending, run] = usePending();
  const purge = () => run("purge", async () => { const r = await api("/admin/analytics/purge-expired", { method: "POST" }); r.ok ? toast.success(`Purged ${r.data.deleted} expired`) : toast.error("Failed"); load(); });
  if (!s) return null;
  const d = s.derived || {};
  const Row = ({ label, obj }) => (
    <div className="kv"><span>{label}</span><b className="small">{Object.entries(obj || {}).map(([k, v]) => `${k}:${v}`).join(" · ") || "—"}</b></div>
  );
  return (
    <div className="card" data-testid="analytics-panel">
      <h3><BarChart3 size={16} /> Product analytics <span className="muted small">(first-party · pseudonymous · {s.retention_days}-day)</span></h3>
      <Row label="invitations" obj={d.invitations} />
      <Row label="participants" obj={d.participants} />
      <Row label="photos" obj={d.photos} />
      <Row label="moderation decisions" obj={d.moderation_human_decisions} />
      <Row label="reports" obj={d.reports} />
      <Row label="viewer" obj={d.viewer} />
      <Row label="account deletions" obj={d.account_deletions} />
      <div className="kv"><span>captured events</span><b className="small" data-testid="analytics-captured">{Object.entries(s.captured_events || {}).map(([k, v]) => `${k}:${v}`).join(" · ") || "none yet"}</b></div>
      <div className="row" style={{ marginTop: 8 }}>
        <button className="btn ghost" onClick={load} data-testid="analytics-refresh"><RefreshCw size={13} /> refresh</button>
        <button className="btn" onClick={purge} disabled={!!pending} data-testid="analytics-purge">{pending === "purge" ? "Purging…" : `Purge expired (>${s.retention_days}d)`}</button>
      </div>
    </div>
  );
}

// Public logged-out landing at "/". Brand hero + a clear Sign in action. Exposes NO authenticated
// functionality; the TV Exit (X) lands here instead of the bare Sign-in panel.
function PublicLanding({ onSignIn }) {
  return (
    <main className="main" data-testid="public-landing" style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
      <header className="hdr">
        <div className="brand auth-brand" data-testid="landing-brand">
          <img src="/brand/www-logo-primary.png" className="auth-logo"
               alt="World Wide Wallpaper — A world of joy, freely shared." />
        </div>
      </header>
      <h1 className="ww-display" data-testid="landing-headline"
          style={{ fontSize: "clamp(34px,5vw,58px)", margin: "10px 0 18px", color: "var(--ww-charcoal)" }}>
        A world of joy, freely shared.
      </h1>
      <p className="muted" style={{ fontSize: 17, lineHeight: 1.6, maxWidth: 540, margin: "0 auto 30px" }}>
        World Wide Wallpaper turns real photographs from people around the world into a calm,
        ever-changing slideshow for your screens — phone, computer and television.
      </p>
      <button className="btn primary" data-testid="landing-signin" onClick={onSignIn}
              style={{ borderRadius: 999, padding: "13px 34px", fontSize: 16, width: "auto", minWidth: 200, display: "inline-flex", justifyContent: "center" }}>
        Sign in
      </button>
      <p className="muted small" style={{ marginTop: 16 }}>
        Members can share photographs, tune their filters and link a TV.
      </p>
    </main>
  );
}

export default function App() {
  // Closed-beta invitation acceptance lives on its own path. Compute the flag
  // before hooks, but return AFTER all hooks so hook order stays stable.
  const isBetaInvite = typeof window !== "undefined" && window.location.pathname.startsWith("/beta/invite");
  const isResetPassword = typeof window !== "undefined" && window.location.pathname.startsWith("/reset-password");
  const isLink = typeof window !== "undefined" && window.location.pathname.startsWith("/link");
  const isTv = typeof window !== "undefined" && window.location.pathname.startsWith("/tv");

  const [session, setSession] = useState(null);
  const [me, setMe] = useState(null);
  const [health, setHealth] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false); // logged-out root: landing -> sign-in toggle

  const refreshMe = useCallback(async () => {
    const r = await api("/me");
    setMe(r.ok ? r.data : null);
  }, []);

  useEffect(() => {
    api("/health", { auth: false }).then((r) => r.ok && setHealth(r.data));
    if (!supabaseConfigured) return;
    // email verify callback (signup only). MUST NOT run on the reset-password
    // pages: those carry their own token_hash/recovery token which ResetPassword
    // consumes via the backend. Calling verifyOtp here would consume the recovery
    // token early AND create a browser session, breaking the reset + sign-out.
    const url = new URL(window.location.href);
    const th = url.searchParams.get("token_hash");
    if (th && !window.location.pathname.startsWith("/reset-password")) {
      const linkType = url.searchParams.get("type") || "signup";
      supabase.auth.verifyOtp({ token_hash: th, type: linkType }).then(({ error }) => {
        // Scrub the one-time token from the address bar / history.
        try { window.history.replaceState({}, document.title, window.location.pathname); } catch (_e) {}
        if (error) {
          toast.error(linkType === "email_change"
            ? "This email-change link is invalid or has expired."
            : "Verification failed");
          return;
        }
        if (linkType === "email_change") {
          toast.success("Your email address has been updated.");
          refreshMe();
        } else {
          toast.success("Email verified — you can log in");
        }
      });
    }
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => { if (session) refreshMe(); else { setMe(null); forgetViewerData(); } }, [session, refreshMe]);

  const logout = async () => {
    if (!window.confirm("Are you sure you want to log out?")) return;
    forgetViewerData(); // never carry one account's viewer preferences into the next sign-in
    await supabase.auth.signOut();
    setMe(null);
  };

  const privileged = me && (me.role === "admin" || me.role === "moderator");
  const needsMfa = privileged && !me.mfa_current;

  const scrollToContributor = () => {
    const el = document.getElementById("ww-contributor");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // Privileged users reach the Moderation/Publication console from a header entry
  // point. If they still need to step up to AAL2, send them to the MFA gate first.
  const scrollToAdmin = () => {
    const el = document.getElementById("ww-admin-console")
      || document.getElementById("ww-mfa-gate")
      || document.getElementById("ww-contributor");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  if (isBetaInvite) return <BetaInvite />;
  if (isResetPassword) return <ResetPassword />;
  if (isTv) return <TvApp />;

  return (
    <>
      <Toaster position="top-right" richColors />
      {!me ? (
        <div className="ww-auth">
          <div className="app" data-testid="app-root">
            {!supabaseConfigured && (
              <div className="banner" data-testid="config-banner">
                <AlertTriangle size={16} /> Supabase publishable key not set in <code>frontend/.env</code>. Auth UI is inert until configured.
              </div>
            )}
            {(isLink || showSignIn) ? (
              <>
                <header className="hdr">
                  <div className="brand auth-brand" data-testid="auth-brand">
                    <img src="/brand/www-logo-primary.png" className="auth-logo"
                         alt="World Wide Wallpaper — A world of joy, freely shared." />
                    <span className="tag">Sign in</span>
                  </div>
                </header>
                {isLink && (
                  <p className="muted" style={{ textAlign: "center", maxWidth: 460, margin: "0 auto" }} data-testid="link-signin-hint">
                    Sign in to link your TV to World Wide Wallpaper.
                  </p>
                )}
                <main className="main"><AuthPanel /></main>
              </>
            ) : (
              <PublicLanding onSignIn={() => setShowSignIn(true)} />
            )}
          </div>
          <div className="ww-footer-standalone"><PoweredBy /></div>
        </div>
      ) : isLink ? (
        <LinkApproval me={me} />
      ) : (
        <div data-testid="app-root">
          <WorldExperience me={me} onLogout={logout} onShare={scrollToContributor} onAccountChanged={refreshMe} privileged={privileged} onAdmin={scrollToAdmin} />

          <section id="ww-contributor" className="ww-app ww-share-section" data-testid="contributor-section" aria-label="Share a photograph">
            <div className="ww-share-wrap">
              <h2 className="ww-section-head ww-display">Share some joy</h2>
              <p className="ww-section-sub">Your photograph could brighten someone's day.</p>
              {needsMfa && <div id="ww-mfa-gate" style={{ marginBottom: 20 }}><MfaEnroll onDone={refreshMe} /></div>}
              <PhotosPanel />
            </div>
          </section>

          {privileged && !needsMfa && (
            <section id="ww-admin-console" className="ww-app" style={{ padding: "0 clamp(20px,5vw,56px) 64px" }} data-testid="admin-console">
              <details open>
                <summary style={{ cursor: "pointer", fontWeight: 600, padding: "14px 0", fontFamily: "var(--font-display)", fontSize: 20 }}>
                  Moderation &amp; publication console · {me.role}
                </summary>
                <div className="main" style={{ marginTop: 12 }}>
                  <div className="card identity" data-testid="identity-card">
                    <h3><CheckCircle2 size={16} className="ok" /> Signed in</h3>
                    <div className="kv"><span>www_user_id</span><b>{me.www_user_id}</b></div>
                    <div className="kv"><span>role</span><b>{me.role}</b></div>
                    <div className="kv"><span>assurance level</span><b className={me.aal === "aal2" ? "ok" : "warn"}>{me.aal || "aal1"}</b></div>
                    <button className="btn ghost" onClick={refreshMe} data-testid="refresh-me"><RefreshCw size={13} /> refresh</button>
                  </div>
                  <ModerationPanel />
                  <PublicationPanel />
                  <SubscriberPanel />
                  {me.role === "admin" && (
                    <>
                      <AdminPanel />
                      <InvitationsPanel />
                      <DeletionsPanel />
                      <AnalyticsPanel />
                    </>
                  )}
                </div>
              </details>
            </section>
          )}

          <SiteFooter />
        </div>
      )}
    </>
  );
}
